import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X, ChevronUp, ChevronDown } from "lucide-react";

// Admin panel for closed-testing applications. Kept intentionally narrow —
// list + inline detail + accept/decline + read-only feedback view. No CSV,
// no bulk actions, no analytics.

const STATUS_LABEL = {
  new: "На рассмотрении",
  accepted: "Одобрено",
  declined: "Не одобрена",
  active_test: "Активно",
  completed_test: "Завершено",
  feedback_received: "Отзыв получен",
};

const STATUS_ORDER = ["new", "accepted", "active_test", "completed_test", "feedback_received", "declined"];

const ADMIN_ACTION_LABEL = {
  accept: "Заявка принята",
  decline: "Заявка не одобрена",
  cancel_accept: "Принятие отменено",
  revert_decline: "Заявка возвращена в новые",
  revoke_active: "Доступ закрыт досрочно",
};

const APPLICATION_LABELS = {
  craft_experience: {
    under_year: "До года", one_to_three_years: "1–3 года", over_three_years: "Больше 3 лет",
  },
  work_mode: {
    hobby: "Хобби", side_income: "Дополнительный заработок", main_work: "Основная работа",
  },
  orders_per_month: {
    zero_to_five: "0–5", six_to_fifteen: "6–15", over_fifteen: "Больше 15",
  },
  order_management_methods: {
    notebook: "Блокнот или ежедневник", phone_notes: "Заметки в телефоне",
    messengers: "Мессенджеры", spreadsheets: "Таблицы",
    crm: "Специальный сервис или CRM", not_systematic: "Никак системно", other: "Другое",
  },
  costing_method: {
    exact: "Всегда считаю точно", not_every_order: "Не для каждого заказа",
    approximate: "Приблизительно", usually_not: "Обычно не считаю",
  },
  recipe_storage_methods: {
    paper: "Бумажные записи", notes: "Заметки", documents: "PDF или документы",
    messengers: "Мессенджеры", spreadsheets: "Таблицы",
    apps: "Приложения или сервисы", other: "Другое",
  },
  planned_device: {
    phone: "Телефон", computer: "Компьютер или ноутбук",
    tablet: "Планшет", multiple: "Несколько устройств",
  },
  brand_style_state: {
    established: "Да, уже есть фирменный стиль",
    elements_no_system: "Есть отдельные элементы, но единого стиля нет",
    want_style: "Пока нет, но хотелось бы оформить",
    not_needed: "Сейчас не нужно",
  },
  desired_brand_assets: {
    logo: "Логотип", business_cards: "Визитки", stickers: "Наклейки", tags: "Бирки",
    ribbons: "Ленты", packaging: "Упаковка", social_media: "Социальные сети", other: "Другое",
  },
};
const CONTINUE_LABEL = { yes: "Да", probably_yes: "Скорее да", unsure: "Пока не уверен", no: "Нет" };
const PUBLICATION_LABEL = { yes: "Да", no: "Нет" };
const LEGACY_PUBLICATION_LABEL = {
  with_name: "Да, с именем", anonymous: "Да, без имени", internal_only: "Нет",
};

function StatusBadge({ status }) {
  const color = {
    new:              { bg: "#F1E4CC", fg: "#5B4326" },
    accepted:         { bg: "#E4F1E7", fg: "#2F5B3D" },
    active_test:      { bg: "#E8ECFA", fg: "#2A3D6B" },
    completed_test:   { bg: "#F5E9DA", fg: "#6B4A2A" },
    feedback_received:{ bg: "#EAE1F2", fg: "#5A3D74" },
    declined:         { bg: "#F0E4E4", fg: "#7A3B3B" },
  }[status] || { bg: "#EFE6D5", fg: "#3E2E20" };
  const Icon = status === "accepted" ? Check : status === "declined" ? X : null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-[0.06em]"
      style={{ background: color.bg, color: color.fg }}
      data-testid={`admin-mo-testing-status-${status}`}
    >
      {Icon && <Icon size={12} strokeWidth={2} aria-hidden="true" />}
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatAnswers(values, labels) {
  if (!Array.isArray(values) || values.length === 0) return "—";
  return values.map((value) => labels[value] || value).join(", ");
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

function TrialContentEditor() {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/master-order-testing/content").then(({ data }) => setValue(data)).catch(() => {}); }, []);
  if (!value) return null;
  const updateQuestion = (group, index, patch) => setValue((current) => ({ ...current,
    [group]: current[group].map((question, i) => i === index ? { ...question, ...patch } : question),
  }));
  const move = (group, index, delta) => setValue((current) => {
    const questions = [...current[group]];
    const target = index + delta;
    if (target < 0 || target >= questions.length) return current;
    [questions[index], questions[target]] = [questions[target], questions[index]];
    return { ...current, [group]: questions };
  });
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch("/admin/master-order-testing/content", value); setValue(data); toast.success("Тексты пробного доступа сохранены"); }
    catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };
  return <details className="rounded-2xl border hairline bg-white p-4" data-testid="owner-trial-content-editor">
    <summary className="cursor-pointer font-serif text-lg text-espresso">Тексты и анкеты 24-часового знакомства</summary>
    <div className="mt-4 space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(value.copy || {}).map(([key, text]) => <label key={key} className="text-xs text-muted-foreground">{key}
          <textarea rows={3} value={text} onChange={(event) => setValue((current) => ({ ...current, copy: { ...current.copy, [key]: event.target.value } }))}
            className="mt-1 w-full rounded-xl border hairline p-3 text-sm text-espresso" />
        </label>)}
      </div>
      {[["pre_trial", "До знакомства"], ["post_trial", "После знакомства"]].map(([group, title]) => <section key={group}>
        <h3 className="font-serif text-lg text-espresso mb-2">{title}</h3>
        <div className="space-y-3">{value[group].map((question, index) => <div key={question.key} className="rounded-xl bg-sand/25 p-3" data-testid={`trial-content-${group}-${question.key}`}>
          <div className="flex gap-2 items-center mb-2">
            <span className="text-xs text-muted-foreground flex-1">{index + 1}. {question.key}</span>
            <button type="button" onClick={() => move(group, index, -1)} disabled={!index} aria-label="Выше"><ChevronUp className="w-4 h-4" /></button>
            <button type="button" onClick={() => move(group, index, 1)} disabled={index === value[group].length - 1} aria-label="Ниже"><ChevronDown className="w-4 h-4" /></button>
          </div>
          <textarea rows={2} value={question.text} onChange={(event) => updateQuestion(group, index, { text: event.target.value })} className="w-full rounded-xl border hairline p-3 text-sm" />
          <label className="mt-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={question.required} onChange={(event) => updateQuestion(group, index, { required: event.target.checked })} /> Обязательный вопрос</label>
          {!!question.options?.length && <div className="mt-2 grid gap-2 sm:grid-cols-2">{question.options.map((option, optionIndex) => <label key={option.value} className="text-[11px] text-muted-foreground">{option.value}
            <input value={option.label} onChange={(event) => updateQuestion(group, index, { options: question.options.map((item, i) => i === optionIndex ? { ...item, label: event.target.value } : item) })} className="mt-1 w-full rounded-lg border hairline px-3 py-2 text-sm text-espresso" />
          </label>)}</div>}
        </div>)}</div>
      </section>)}
      <button type="button" onClick={save} disabled={saving} className="h-10 px-5 rounded-xl bg-espresso text-white text-sm" data-testid="owner-trial-content-save">{saving ? "Сохраняем…" : "Сохранить тексты"}</button>
    </div>
  </details>;
}

export default function MOTestingAdmin() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [historyByApp, setHistoryByApp] = useState({});
  const [actionPending, setActionPending] = useState(null); // id being accepted/declined
  const [intakeOpen, setIntakeOpen] = useState(null); // null while loading
  const [intakePending, setIntakePending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // accepted/declined application awaiting confirmation
  const [revokeTarget, setRevokeTarget] = useState(null); // application currently being revoked

  const testingFormAbsoluteUrl = useMemo(
    () => (typeof window !== "undefined" ? `${window.location.origin}/master-order-testing` : "/master-order-testing"),
    []
  );

  const openForm = () => window.open(testingFormAbsoluteUrl, "_blank", "noopener,noreferrer");

  const copyFormLink = async () => {
    try {
      await navigator.clipboard.writeText(testingFormAbsoluteUrl);
      setCopied(true);
      toast.success("Ссылка скопирована");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback: create a hidden textarea (older Safari / non-secure contexts).
      const t = document.createElement("textarea");
      t.value = testingFormAbsoluteUrl;
      document.body.appendChild(t);
      t.select();
      try { document.execCommand("copy"); setCopied(true); toast.success("Ссылка скопирована"); }
      catch { toast.error("Не удалось скопировать"); }
      finally { document.body.removeChild(t); setTimeout(() => setCopied(false), 1600); }
    }
  };

  const toggleIntake = async () => {
    if (intakePending || intakeOpen === null) return;
    const next = !intakeOpen;
    setIntakePending(true);
    try {
      const { data } = await api.post("/admin/master-order-testing/intake-status", { open: next });
      setIntakeOpen(!!data.open);
      toast.success(data.open ? "Приём заявок открыт" : "Приём заявок закрыт");
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось обновить статус");
    } finally {
      setIntakePending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/master-order-testing/applications");
      setApplications(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось загрузить заявки");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master-order-testing/intake-status");
        setIntakeOpen(!!data?.open);
      } catch { /* keep null → hide toggle */ }
    })();
  }, []);

  const sorted = useMemo(() => {
    const arr = [...applications];
    arr.sort((a, b) => {
      const sa = STATUS_ORDER.indexOf(a.status);
      const sb = STATUS_ORDER.indexOf(b.status);
      if (sa !== sb) return sa - sb;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return arr;
  }, [applications]);

  const selected = useMemo(
    () => applications.find((a) => a.id === selectedId) || null,
    [applications, selectedId]
  );

  const loadHistory = async (id, force = false) => {
    if (!force && historyByApp[id]) return;
    try {
      const { data } = await api.get(
        `/admin/master-order-testing/applications/${id}/history`
      );
      setHistoryByApp((prev) => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось загрузить историю действий");
    }
  };

  const toggleDetails = async (id) => {
    if (selectedId === id) {
      setSelectedId(null);
      return;
    }
    setSelectedId(id);
    await loadHistory(id);
  };

  const doAction = async (id, action) => {
    setActionPending(id);
    try {
      const { data } = await api.post(`/admin/master-order-testing/applications/${id}/${action}`);
      setApplications((prev) => prev.map((a) => (a.id === id ? data : a)));
      if (historyByApp[id] || selectedId === id) await loadHistory(id, true);
      const msg = ({
        "accept":         "Заявка принята",
        "decline":        "Заявка не одобрена",
        "cancel-accept":  "Принятие отменено, заявка снова новая",
        "revert-decline": "Заявка возвращена в новые",
        "revoke-active":  "Тестовый доступ закрыт досрочно",
      })[action] || "Готово";
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось выполнить действие");
    } finally {
      setActionPending(null);
    }
  };

  const copyTestimonial = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Отзыв скопирован");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const canPreview = ["owner", "administrator"].includes(user?.role);

  return (
    <div data-testid="admin-mo-testing-panel" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-espresso mb-1" data-testid="admin-mo-testing-title">
          Отзывы о Мастере заказа
        </h2>
        <p className="text-sm text-muted-foreground">
          Заявки на 24-часовое знакомство с Мастером заказа.
        </p>
        {/* Direct actions — placed under the heading, visible on desktop AND mobile.
            "Скопировать ссылку" writes the absolute testing URL of the current
            domain into the clipboard. */}
        <div
          className="mt-3 flex flex-wrap items-center gap-2"
          data-testid="admin-mo-testing-quick-actions"
        >
          {canPreview && <button
            type="button"
            onClick={() => nav("/master-order-testing?preview_state=application")}
            data-testid="admin-mo-testing-preview-form"
            className="h-9 px-4 rounded-xl text-[12.5px]"
            style={{ background: "#4A3728", color: "#F7EFE0" }}
          >
            Предпросмотр анкеты
          </button>}
          <button
            type="button"
            onClick={openForm}
            data-testid="admin-mo-testing-open-form"
            className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
            style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
          >
            Открыть публичную страницу
          </button>
          <button
            type="button"
            onClick={copyFormLink}
            data-testid="admin-mo-testing-copy-link"
            className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
            style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
          >
            {copied ? "Ссылка скопирована" : "Скопировать ссылку"}
          </button>
        </div>

        {/* Intake open/closed toggle — persisted server-side via app_settings. */}
        {intakeOpen !== null && (
          <div
            className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl px-4 py-2.5"
            style={{ background: intakeOpen ? "#E8F0DF" : "#F5E1E1", color: intakeOpen ? "#3B4A2A" : "#7A3B3B" }}
            data-testid="admin-mo-testing-intake-toggle"
          >
            <span className="text-[12.5px]">
              {intakeOpen ? "Приём заявок открыт" : "Приём заявок закрыт"}
            </span>
            <button
              type="button"
              onClick={toggleIntake}
              disabled={intakePending}
              data-testid="admin-mo-testing-intake-toggle-button"
              className="h-8 px-3 rounded-xl text-[12px] bg-white"
              style={{ boxShadow: "inset 0 0 0 1px #C7A97A", color: "#5B4326" }}
            >
              {intakePending ? "Обновляем…" : (intakeOpen ? "Закрыть приём" : "Открыть приём")}
            </button>
          </div>
        )}
      </div>

      {canPreview &&
        <div className="rounded-2xl border hairline bg-white p-4" data-testid="owner-trial-preview-states">
          <div className="font-serif text-lg text-espresso mb-3">Предпросмотр — без записи данных</div>
          <div className="flex flex-wrap gap-2">{[
            ["application", "Анкета"], ["pending", "На рассмотрении"], ["approved_not_started", "Одобрено"],
            ["active", "Активно"], ["completed", "Завершено"], ["feedback_form", "Форма отзыва"],
            ["feedback_received", "Отзыв получен"],
          ].map(([state, label]) => <button key={state} type="button" onClick={() => nav(state === "feedback_form" ? `/master-order-testing/feedback?preview_state=${state}` : `/master-order-testing?preview_state=${state}`)}
            className="h-9 px-3 rounded-xl border border-espresso/30 text-xs text-espresso" data-testid={`owner-preview-${state}`}>{label}</button>)}</div>
        </div>}
      {user?.role === "owner" && <TrialContentEditor />}

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="admin-mo-testing-loading">
          Загружаем заявки…
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl bg-sand/40 border border-sand text-espresso px-4 py-6 text-sm" data-testid="admin-mo-testing-empty">
          Пока нет ни одной заявки.
        </div>
      ) : (
        <div className="grid gap-3" data-testid="admin-mo-testing-list">
          {sorted.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-mo-testing-row-${a.id}`}
              className="rounded-2xl bg-white border border-sand/70 p-4 sm:p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[15px] font-medium text-espresso truncate">
                    {a.user_name_snapshot || "—"}
                  </div>
                  <div className="text-[12.5px] text-muted-foreground truncate">
                    {a.user_email || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={a.status} />
                </div>
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[13px] text-espresso">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Ссылка</div>
                  <a href={a.social_url} target="_blank" rel="noreferrer"
                    className="underline underline-offset-2 break-all" style={{ color: "#5B4326" }}
                    data-testid={`admin-mo-testing-social-${a.id}`}>{a.social_url}</a>
                </div>
                <AdminAnswer label="Стаж" value={APPLICATION_LABELS.craft_experience[a.craft_experience] || "—"} />
                <AdminAnswer label="Формат работы" value={APPLICATION_LABELS.work_mode[a.work_mode] || "—"} />
                <AdminAnswer label="Заказов в месяц" value={APPLICATION_LABELS.orders_per_month[a.orders_per_month] || "—"} />
                <AdminAnswer label="Как ведёт заказы" value={formatAnswers(a.order_management_methods, APPLICATION_LABELS.order_management_methods)} />
                <AdminAnswer label="Как считает себестоимость" value={APPLICATION_LABELS.costing_method[a.costing_method] || "—"} />
                <AdminAnswer label="Где хранит рецепты" value={formatAnswers(a.recipe_storage_methods, APPLICATION_LABELS.recipe_storage_methods)} />
                <AdminAnswer label="Устройство" value={APPLICATION_LABELS.planned_device[a.planned_device] || "—"} />
                <AdminAnswer label="Оформление бренда" value={APPLICATION_LABELS.brand_style_state[a.brand_style_state] || "—"} />
                <AdminAnswer label="Что хотелось бы оформить" value={formatAnswers(a.desired_brand_assets, APPLICATION_LABELS.desired_brand_assets)} />
                {a.country && <AdminAnswer label="Страна (историческое поле)" value={a.country} />}
                <AdminAnswer label="Дата заявки" value={formatDate(a.created_at)} />
                <AdminAnswer label="Статус отзыва" value={a.feedback ? "Отзыв получен" : "Ожидаем отзыв"} />
              </div>
              {a.desired_help_text && (
                <div className="mt-3 text-[13px] text-espresso">
                  <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">Что хотелось бы упростить</div>
                  <div className="whitespace-pre-wrap">{a.desired_help_text}</div>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {a.status === "new" && (
                  <>
                    <button
                      type="button"
                      onClick={() => doAction(a.id, "accept")}
                      disabled={actionPending === a.id}
                      className="h-9 px-4 rounded-xl text-[12.5px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid={`admin-mo-testing-accept-${a.id}`}
                    >
                      Принять
                    </button>
                    <button
                      type="button"
                      onClick={() => doAction(a.id, "decline")}
                      disabled={actionPending === a.id}
                      className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
                      style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                      data-testid={`admin-mo-testing-decline-${a.id}`}
                    >
                      Не одобрить
                    </button>
                  </>
                )}
                {a.status === "accepted" && (
                  <button
                    type="button"
                    onClick={() => setResetTarget({ application: a, action: "cancel-accept" })}
                    disabled={actionPending === a.id}
                    className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
                    style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                    data-testid={`admin-mo-testing-cancel-accept-${a.id}`}
                  >
                    Отменить принятие
                  </button>
                )}
                {a.status === "declined" && (
                  <button
                    type="button"
                    onClick={() => setResetTarget({ application: a, action: "revert-decline" })}
                    disabled={actionPending === a.id}
                    className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
                    style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                    data-testid={`admin-mo-testing-revert-decline-${a.id}`}
                  >
                    Вернуть в новые
                  </button>
                )}
                {a.status === "active_test" && (
                  <button
                    type="button"
                    onClick={() => setRevokeTarget(a)}
                    className="h-9 px-4 rounded-xl text-[12.5px]"
                    style={{ background: "#7A2E17", color: "#F7EFE0" }}
                    data-testid={`admin-mo-testing-revoke-${a.id}`}
                  >
                    Закрыть доступ досрочно
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => toggleDetails(a.id)}
                  className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
                  style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #E4D3B0" }}
                  data-testid={`admin-mo-testing-toggle-${a.id}`}
                >
                  {selectedId === a.id ? "Свернуть" : "Подробнее"}
                </button>
              </div>

              {selectedId === a.id && (
                <div
                  className="mt-4 pt-4 border-t border-sand/70 grid gap-3 text-[13px] text-espresso"
                  data-testid={`admin-mo-testing-detail-${a.id}`}
                >
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><span className="text-muted-foreground">Принята: </span>{formatDate(a.accepted_at)}</div>
                    <div><span className="text-muted-foreground">Не одобрена: </span>{formatDate(a.declined_at)}</div>
                    <div><span className="text-muted-foreground">Доступ открыт с: </span>{formatDate(a.access_started_at || a.test_access_started_at)}</div>
                    <div><span className="text-muted-foreground">Доступ до: </span>{formatDate(a.access_expires_at || a.test_access_expires_at)}</div>
                    <div><span className="text-muted-foreground">Знакомство завершено: </span>{formatDate(a.completed_at)}</div>
                    <div><span className="text-muted-foreground">Отзыв отправлен: </span>{formatDate(a.feedback_submitted_at)}</div>
                    <div><span className="text-muted-foreground">Досрочно закрыт: </span>{formatDate(a.revoked_at)}</div>
                    <div><span className="text-muted-foreground">Закрыл администратор: </span>{a.revoked_by || "—"}</div>
                  </div>

                  <div className="rounded-xl bg-white/70 border border-sand/70 p-4">
                    <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-2">
                      История административных действий
                    </div>
                    {(historyByApp[a.id] || []).length > 0 ? (
                      <ul className="space-y-2" data-testid={`admin-mo-testing-history-${a.id}`}>
                        {historyByApp[a.id].map((item) => (
                          <li key={item.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <span>{ADMIN_ACTION_LABEL[item.action] || item.action}</span>
                            <span className="text-[11.5px] text-muted-foreground">
                              {formatDate(item.created_at)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-[12.5px] text-muted-foreground">
                        История появится после следующего административного действия.
                      </div>
                    )}
                  </div>

                  {a.feedback ? (
                    <div className="rounded-xl bg-sand/30 border border-sand/70 p-4 space-y-3"
                      data-testid={`admin-mo-testing-feedback-${a.id}`}>
                      <AdminAnswer label="Понятность, 1–5" value={a.feedback.clarity_rating || "—"} />
                      <AdminAnswer label="Самое удобное или полезное" value={a.feedback.most_useful_text || a.feedback.unexpectedly_convenient_text || "—"} />
                      <AdminAnswer label="Где возникли вопросы" value={a.feedback.confusing_text || a.feedback.first_pause_text || "—"} />
                      <AdminAnswer label="Чего не хватило" value={a.feedback.missing_text || a.feedback.expected_missing_text || "—"} />
                      <AdminAnswer label="Что улучшить в первую очередь" value={a.feedback.priority_improvement_text || a.feedback.replacement_text || a.feedback.unnecessary_step_text || "—"} />
                      <AdminAnswer label="Желание продолжать" value={CONTINUE_LABEL[a.feedback.continue_use || a.feedback.next_real_order] || "—"} />
                      <AdminAnswer label="Отправлено" value={formatDate(a.feedback.created_at)} />
                      <div className="text-[12.5px]">
                        <span className="text-muted-foreground">Согласие на использование: </span>
                        <span data-testid={`admin-mo-testing-feedback-permission-${a.id}`}>
                          {PUBLICATION_LABEL[a.feedback.publication_consent] || LEGACY_PUBLICATION_LABEL[a.feedback.publication_permission] || "Не выбрано"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[12.5px] text-muted-foreground">Отзыв пока не получен.</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {resetTarget && (
        <div
          data-testid="admin-mo-testing-reset-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setResetTarget(null)}
        >
          <div
            className="max-w-md w-full rounded-2xl bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-serif text-xl text-espresso mb-2">
              {resetTarget.action === "cancel-accept" ? "Отменить принятие?" : "Вернуть заявку в новые?"}
            </div>
            <p className="text-[13.5px] text-espresso/80 mb-4">
              {resetTarget.action === "cancel-accept"
                ? "Заявка снова станет новой. Тестовый доступ и 24-часовой период не будут созданы."
                : "Статус «Не одобрена» будет снят, и заявка снова появится среди новых."}
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
                style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                data-testid="admin-mo-testing-reset-cancel"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { application, action } = resetTarget;
                  setResetTarget(null);
                  await doAction(application.id, action);
                }}
                className="h-9 px-4 rounded-xl text-[12.5px]"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="admin-mo-testing-reset-confirm"
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}
      {revokeTarget && (
        <div
          data-testid="admin-mo-testing-revoke-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setRevokeTarget(null)}
        >
          <div
            className="max-w-md w-full rounded-2xl bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-serif text-xl text-espresso mb-2">Закрыть доступ досрочно?</div>
            <p className="text-[13.5px] text-espresso/80 mb-4">
              Тестовый доступ для «{revokeTarget.user_name_snapshot || revokeTarget.user_email}»
              будет закрыт немедленно. Заявка, отзыв и сохранённые материалы участника не удаляются.
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                className="h-9 px-4 rounded-xl text-[12.5px] bg-transparent"
                style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
                data-testid="admin-mo-testing-revoke-cancel"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={async () => {
                  const id = revokeTarget.id;
                  setRevokeTarget(null);
                  await doAction(id, "revoke-active");
                }}
                className="h-9 px-4 rounded-xl text-[12.5px]"
                style={{ background: "#7A2E17", color: "#F7EFE0" }}
                data-testid="admin-mo-testing-revoke-confirm"
              >
                Закрыть доступ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AdminAnswer({ label, value }) {
  return <div>
    <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground mb-0.5">{label}</div>
    <div className="whitespace-pre-wrap">{value}</div>
  </div>;
}
